// /src/logic/csvScheduleValidation.js

/**
 * validateCsvData
 * Parses and validates CSV content for required fields:
 *  EventName, Description, StartDate, StartTime, EndTime, Location
 *
 * User-friendly behavior:
 *  - Accepts StartDate formats:
 *      YYYY-MM-DD
 *      M/D/YYYY
 *      MM/DD/YYYY
 *      M-D-YYYY
 *      MM-DD-YYYY
 *  - Converts StartDate to YYYY-MM-DD
 *  - Accepts StartTime/EndTime formats:
 *      9:00 AM
 *      9:00AM
 *      09:00
 *      14:30
 *  - Converts times to HH:mm 24-hour format
 *  - Supports quoted CSV fields with commas inside them
 *  - Provides clearer row-level error messages
 *
 * @param {string} csvContent - The raw text content of the CSV file
 * @returns {Array} - Array of validated/converted Event objects
 * @throws {Error} - Throws error if format or data is invalid
 */
export const validateCsvData = (csvContent) => {
  const requiredHeaders = [
    "EventName",
    "Description",
    "StartDate",
    "StartTime",
    "EndTime",
    "Location",
  ];

  const maxLengths = {
    EventName: 80,
    Description: 500,
    Location: 120,
  };

  const normalizeLineEndings = (text) => {
    return text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  };

  const parseCsvLine = (line) => {
    const values = [];
    let current = "";
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"' && insideQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === "," && !insideQuotes) {
        values.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    values.push(current.trim());

    return values;
  };

  const formatDateParts = (year, month, day) => {
    const yyyy = String(year);
    const mm = String(month).padStart(2, "0");
    const dd = String(day).padStart(2, "0");

    const date = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);

    if (
      Number.isNaN(date.getTime()) ||
      date.getFullYear() !== Number(year) ||
      date.getMonth() + 1 !== Number(month) ||
      date.getDate() !== Number(day)
    ) {
      return null;
    }

    return `${yyyy}-${mm}-${dd}`;
  };

  const normalizeDate = (dateStr, rowIndex) => {
    const cleanStr = String(dateStr || "").trim();

    if (!cleanStr) {
      throw new Error(`Row #${rowIndex} is missing "StartDate".`);
    }

    const isoMatch = cleanStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      const formatted = formatDateParts(year, month, day);

      if (!formatted) {
        throw new Error(
          `Row #${rowIndex} has invalid "StartDate": "${dateStr}".`
        );
      }

      return formatted;
    }

    const slashOrDashMatch = cleanStr.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (slashOrDashMatch) {
      const [, month, day, year] = slashOrDashMatch;
      const formatted = formatDateParts(year, month, day);

      if (!formatted) {
        throw new Error(
          `Row #${rowIndex} has invalid "StartDate": "${dateStr}".`
        );
      }

      return formatted;
    }

    throw new Error(
      `Row #${rowIndex} has invalid "StartDate": "${dateStr}". Use YYYY-MM-DD or MM/DD/YYYY.`
    );
  };

  const convertTo24HourFormat = (timeStr, rowIndex, fieldName) => {
    const cleanStr = String(timeStr || "")
      .trim()
      .replace(/\s+/g, "")
      .toUpperCase();

    if (!cleanStr) {
      throw new Error(`Row #${rowIndex} is missing "${fieldName}".`);
    }

    const timeRegex = /^(\d{1,2})(?::(\d{2}))?([AP]M)?$/;
    const match = cleanStr.match(timeRegex);

    if (!match) {
      throw new Error(
        `Row #${rowIndex} has invalid "${fieldName}" time: "${timeStr}". Use 9:00 AM, 09:00, or 14:30.`
      );
    }

    let [, hour, minute = "00", meridiem] = match;
    let h = parseInt(hour, 10);
    const m = parseInt(minute, 10);

    if (m < 0 || m > 59) {
      throw new Error(`Row #${rowIndex} has invalid minute in "${fieldName}".`);
    }

    if (meridiem) {
      if (h < 1 || h > 12) {
        throw new Error(
          `Row #${rowIndex} has invalid hour in "${fieldName}". Use 1-12 with AM/PM.`
        );
      }

      if (meridiem === "AM") {
        if (h === 12) h = 0;
      } else if (h !== 12) {
        h += 12;
      }
    } else if (h < 0 || h > 23) {
      throw new Error(
        `Row #${rowIndex} has invalid hour in "${fieldName}". Use 0-23 for 24-hour time.`
      );
    }

    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");

    return `${hh}:${mm}`;
  };

  const validateLength = (value, fieldName, rowIndex) => {
    const maxLength = maxLengths[fieldName];

    if (maxLength && value.length > maxLength) {
      throw new Error(
        `Row #${rowIndex} "${fieldName}" is too long. Maximum length is ${maxLength} characters.`
      );
    }
  };

  const lines = normalizeLineEndings(csvContent)
    .split("\n")
    .filter((line) => line.trim());

  if (lines.length < 2) {
    throw new Error("CSV must include one header row and at least one event row.");
  }

  const headers = parseCsvLine(lines[0]);

  if (headers.length < requiredHeaders.length) {
    throw new Error(`CSV must have these columns: ${requiredHeaders.join(", ")}`);
  }

  for (let i = 0; i < requiredHeaders.length; i++) {
    if (headers[i] !== requiredHeaders[i]) {
      throw new Error(
        `Invalid CSV header. Expected "${requiredHeaders[i]}" but found "${headers[i]}".`
      );
    }
  }

  const validatedEvents = [];
  const rowErrors = [];

  for (let lineIndex = 1; lineIndex < lines.length; lineIndex++) {
    const rowIndex = lineIndex + 1;
    const row = lines[lineIndex].trim();

    if (!row) continue;

    try {
      const columns = parseCsvLine(row);

      if (columns.length < requiredHeaders.length) {
        throw new Error(
          `Row #${rowIndex} has too few columns. Make sure each row has EventName, Description, StartDate, StartTime, EndTime, and Location.`
        );
      }

      const eventObj = {
        EventName: columns[0].trim(),
        Description: columns[1].trim(),
        StartDate: columns[2].trim(),
        StartTime: columns[3].trim(),
        EndTime: columns[4].trim(),
        Location: columns[5].trim(),
      };

      if (!eventObj.EventName) {
        throw new Error(`Row #${rowIndex} is missing "EventName".`);
      }

      if (!eventObj.Location) {
        throw new Error(`Row #${rowIndex} is missing "Location".`);
      }

      validateLength(eventObj.EventName, "EventName", rowIndex);
      validateLength(eventObj.Description, "Description", rowIndex);
      validateLength(eventObj.Location, "Location", rowIndex);

      eventObj.StartDate = normalizeDate(eventObj.StartDate, rowIndex);
      eventObj.StartTime = convertTo24HourFormat(
        eventObj.StartTime,
        rowIndex,
        "StartTime"
      );
      eventObj.EndTime = convertTo24HourFormat(
        eventObj.EndTime,
        rowIndex,
        "EndTime"
      );

      validatedEvents.push(eventObj);
    } catch (error) {
      rowErrors.push(error.message);
    }
  }

  if (rowErrors.length > 0) {
    throw new Error(rowErrors.join("\n"));
  }

  if (validatedEvents.length === 0) {
    throw new Error("CSV did not contain any valid event rows.");
  }

  return validatedEvents;
};